import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import api, { apiErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import { getDisplayTitle } from '@/lib/displayTitle';
import {
    X, Loader2, Mail, Send, Sparkles, History, CheckCircle2, XCircle,
} from 'lucide-react';

const VIOLET = '#7C6BEF';

const TEMPLATES = [
    { code: 'custom', label: 'Mensaje libre', requestRequired: false },
    { code: 'scheduled', label: 'Auditoría programada', requestRequired: true },
    { code: 'result', label: 'Resultado de auditoría', requestRequired: true },
];

/**
 * EmailComposerModal — Admin composes and sends emails to a client, with
 * optional AI polish. Also shows recent email history for the client.
 */
export default function EmailComposerModal({ clientId, clientLabel, clientEmail, distintivos, onClose }) {
    const [template, setTemplate] = useState('custom');
    const [role, setRole] = useState('admin');
    const [requestId, setRequestId] = useState('');
    const [requests, setRequests] = useState([]);
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');
    const [tone, setTone] = useState('formal');
    const [sending, setSending] = useState(false);
    const [improving, setImproving] = useState(false);
    const [history, setHistory] = useState([]);
    const [showHistory, setShowHistory] = useState(false);
    const [loadingHistory, setLoadingHistory] = useState(false);

    const currentTemplate = useMemo(
        () => TEMPLATES.find((t) => t.code === template) || TEMPLATES[0],
        [template],
    );
    const distLookup = useMemo(() => Object.fromEntries((distintivos || []).map((d) => [d.id, d])), [distintivos]);

    useEffect(() => {
        // Load client's recent certification requests (for scheduled/result templates)
        api.get(`/admin/clients/${clientId}/detail`)
            .then((r) => {
                const all = [
                    ...(r.data?.groups?.scheduled || []),
                    ...(r.data?.groups?.in_progress || []),
                    ...(r.data?.groups?.completed || []),
                ];
                setRequests(all);
            })
            .catch(() => setRequests([]));
    }, [clientId]);

    const loadHistory = async () => {
        setLoadingHistory(true);
        try {
            const r = await api.get(`/admin/clients/${clientId}/emails`);
            setHistory(r.data || []);
        } catch (e) {
            toast.error(apiErrorMessage(e, 'Error al cargar historial'));
        } finally {
            setLoadingHistory(false);
        }
    };

    const toggleHistory = () => {
        setShowHistory((prev) => {
            if (!prev) loadHistory();
            return !prev;
        });
    };

    const improveWithAI = async () => {
        if (!body.trim()) {
            toast.error('Escribe el cuerpo antes de mejorar con IA');
            return;
        }
        setImproving(true);
        try {
            const r = await api.post('/admin/emails/improve', {
                subject, body, tone,
                context: `Cliente: ${clientLabel || ''}`,
            });
            setSubject(r.data?.subject || subject);
            setBody(r.data?.body || body);
            toast.success('Correo mejorado con IA');
        } catch (e) {
            toast.error(apiErrorMessage(e, 'Error al mejorar con IA'));
        } finally {
            setImproving(false);
        }
    };

    const sendEmail = async () => {
        if (currentTemplate.code === 'custom') {
            if (!subject.trim() || !body.trim()) {
                toast.error('Asunto y cuerpo son requeridos');
                return;
            }
        } else if (!requestId) {
            toast.error('Selecciona una auditoría para esta plantilla');
            return;
        }
        setSending(true);
        try {
            const payload = {
                client_id: clientId,
                template: currentTemplate.code,
                role,
                request_id: requestId || undefined,
                subject: subject || undefined,
                body: body || undefined,
            };
            await api.post('/admin/emails/send', payload);
            toast.success('Correo enviado');
            if (showHistory) loadHistory();
            // Reset custom body only
            if (currentTemplate.code === 'custom') {
                setSubject('');
                setBody('');
            }
        } catch (e) {
            toast.error(apiErrorMessage(e, 'Error al enviar correo'));
        } finally {
            setSending(false);
        }
    };

    return createPortal(
        <div
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
            onClick={onClose}
            data-testid="email-composer-backdrop"
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-3xl my-8"
                style={{ background: 'var(--card-bg)', border: `2px solid ${VIOLET}` }}
                data-testid="email-composer-modal"
            >
                {/* Header */}
                <div className="flex items-start justify-between px-6 py-4 border-b" style={{ borderColor: 'var(--border)' }}>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 flex items-center justify-center" style={{ background: `${VIOLET}22`, color: VIOLET }}>
                            <Mail className="w-5 h-5" />
                        </div>
                        <div>
                            <div className="text-[11px] uppercase tracking-[0.2em] font-bold" style={{ color: VIOLET }}>Comunicación</div>
                            <h2 className="font-display text-xl font-black">Enviar correo</h2>
                            <div className="text-xs text-gray-500 mt-0.5">
                                Para: <strong>{clientLabel}</strong> {clientEmail && <span className="text-gray-400">· {clientEmail}</span>}
                            </div>
                        </div>
                    </div>
                    <button type="button" onClick={onClose} data-testid="email-composer-close-btn" className="p-2 hover:bg-black/5 dark:hover:bg-white/10">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Config bar */}
                <div className="px-6 py-3 border-b grid grid-cols-1 md:grid-cols-3 gap-3" style={{ borderColor: 'var(--border)' }}>
                    <SelectField label="Plantilla" value={template} onChange={setTemplate} testId="email-template">
                        {TEMPLATES.map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
                    </SelectField>
                    <SelectField label="Remitente" value={role} onChange={setRole} testId="email-role">
                        <option value="admin">Notificaciones (admin)</option>
                        <option value="auditor">Auditorías</option>
                    </SelectField>
                    {currentTemplate.requestRequired ? (
                        <SelectField label="Auditoría" value={requestId} onChange={setRequestId} testId="email-request">
                            <option value="">Selecciona…</option>
                            {requests.map((r) => (
                                <option key={r.id} value={r.id}>
                                    {getDisplayTitle(r, distLookup).title} · {r.status}
                                </option>
                            ))}
                        </SelectField>
                    ) : (
                        <SelectField label="Tono (para IA)" value={tone} onChange={setTone} testId="email-tone">
                            <option value="formal">Formal</option>
                            <option value="friendly">Cordial</option>
                            <option value="technical">Técnico</option>
                        </SelectField>
                    )}
                </div>

                {/* Body — only for custom template */}
                {currentTemplate.code === 'custom' ? (
                    <div className="px-6 py-4 space-y-3">
                        <div>
                            <label className="text-xs uppercase tracking-wider font-bold mb-1 block text-gray-500">Asunto</label>
                            <input
                                type="text"
                                value={subject}
                                onChange={(e) => setSubject(e.target.value)}
                                placeholder="Asunto del correo"
                                data-testid="email-subject-input"
                                className="w-full px-3 py-2 border outline-none text-sm"
                                style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }}
                            />
                        </div>
                        <div>
                            <label className="text-xs uppercase tracking-wider font-bold mb-1 block text-gray-500">Cuerpo del mensaje</label>
                            <textarea
                                rows={9}
                                value={body}
                                onChange={(e) => setBody(e.target.value)}
                                placeholder="Escribe el contenido del correo…"
                                data-testid="email-body-input"
                                className="w-full px-3 py-2 border outline-none text-sm resize-y"
                                style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }}
                            />
                            <div className="text-[11px] text-gray-500 mt-1">
                                Saltos de línea dobles = nuevo párrafo. El correo se envía con el branding AIGIS aplicado automáticamente.
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                        Esta plantilla se genera automáticamente con los datos de la auditoría seleccionada.
                        No requiere redacción manual — al enviar se usará la plantilla oficial de AIGIS.
                    </div>
                )}

                {/* History panel */}
                {showHistory && (
                    <div className="px-6 py-3 border-t" style={{ borderColor: 'var(--border)', background: 'var(--muted-bg, transparent)' }}>
                        <div className="text-xs uppercase tracking-[0.15em] font-black mb-2" style={{ color: VIOLET }}>Historial reciente</div>
                        {loadingHistory ? (
                            <div className="text-xs text-gray-500 py-2"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Cargando…</div>
                        ) : history.length === 0 ? (
                            <div className="text-xs text-gray-400 italic py-2">Aún no se han enviado correos a este cliente.</div>
                        ) : (
                            <div className="space-y-1 max-h-48 overflow-y-auto text-xs" data-testid="email-history-list">
                                {history.map((h) => (
                                    <div key={h.id} className="flex items-start gap-2 p-2 border" style={{ borderColor: 'var(--border)' }}>
                                        {h.status === 'sent'
                                            ? <CheckCircle2 className="w-3.5 h-3.5 shrink-0 mt-0.5 text-green-600" />
                                            : <XCircle className="w-3.5 h-3.5 shrink-0 mt-0.5 text-red-600" />}
                                        <div className="flex-1 min-w-0">
                                            <div className="font-bold truncate">{h.subject}</div>
                                            <div className="text-gray-500">
                                                {new Date(h.created_at).toLocaleString('es')} · {h.template} · {h.to}
                                                {h.status === 'failed' && h.error && (
                                                    <span className="text-red-500 ml-1">· {h.error.slice(0, 80)}</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Footer */}
                <div className="px-6 py-4 border-t flex flex-wrap gap-2 justify-between items-center" style={{ borderColor: 'var(--border)' }}>
                    <button
                        type="button"
                        onClick={toggleHistory}
                        data-testid="email-toggle-history-btn"
                        className="inline-flex items-center gap-2 text-xs font-bold text-gray-500 hover:text-gray-900 dark:hover:text-gray-100"
                    >
                        <History className="w-3.5 h-3.5" /> {showHistory ? 'Ocultar historial' : 'Ver historial'}
                    </button>
                    <div className="flex gap-2">
                        {currentTemplate.code === 'custom' && (
                            <button
                                type="button"
                                onClick={improveWithAI}
                                disabled={improving || sending}
                                data-testid="email-improve-ai-btn"
                                className="inline-flex items-center gap-2 px-4 py-2 border-2 font-bold text-sm disabled:opacity-40"
                                style={{ borderColor: '#E08B3C', color: '#E08B3C' }}
                            >
                                {improving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                                Mejorar con IA
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={sendEmail}
                            disabled={sending || improving}
                            data-testid="email-send-btn"
                            className="inline-flex items-center gap-2 px-5 py-2 text-white font-bold text-sm disabled:opacity-40"
                            style={{ background: VIOLET }}
                        >
                            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                            Enviar correo
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body,
    );
}

function SelectField({ label, value, onChange, children, testId }) {
    return (
        <div>
            <label className="text-xs uppercase tracking-wider font-bold mb-1 block text-gray-500">{label}</label>
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testId}
                className="w-full px-3 py-2 border outline-none text-sm"
                style={{ borderColor: 'var(--border)', background: 'var(--card-bg)', color: 'var(--foreground)' }}
            >
                {children}
            </select>
        </div>
    );
}
